import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { fmtDay, fmtLakhValue, fmtPct } from "@/lib/api";

const sum = (rows, read) => rows.reduce((t, r) => t + (read(r) || 0), 0);

/**
 * Unit by unit, what each plant is sending.
 *
 * Built from the rows the screen already has, so it can never disagree with them. A unit opens to
 * its own use cases with the day each was last filled and who filled it.
 */
export const UnitCompliance = ({ rows = [] }) => {
  const [open, setOpen] = useState("");
  const units = [...new Set(rows.map((r) => r.plant))].sort();

  if (!units.length)
    return (
      <p className="text-sm text-muted-foreground" data-testid="compliance-units-empty">
        No unit is sending compliance data in this view yet.
      </p>
    );

  return (
    <div className="divide-y divide-border border border-border" data-testid="compliance-units">
      {units.map((unit) => {
        const mine = rows.filter((r) => r.plant === unit);
        const pcts = mine
          .map((r) => r.compliance.cumulative_pct)
          .filter((p) => p !== null && p !== undefined);
        const latest = mine
          .map((r) => r.last_updated)
          .filter(Boolean)
          .sort()
          .pop();
        const isOpen = open === unit;
        return (
          <div key={unit}>
            <button
              type="button"
              data-testid={`compliance-unit-${unit}`}
              onClick={() => setOpen(isOpen ? "" : unit)}
              className="flex w-full flex-wrap items-center justify-between gap-4 bg-card px-4 py-3 text-left transition-colors duration-200 hover:bg-muted"
            >
              <span className="flex min-w-0 items-center gap-3">
                <ChevronDown
                  className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${
                    isOpen ? "rotate-0" : "-rotate-90"
                  }`}
                />
                <span className="font-display text-sm font-bold tracking-tight">{unit}</span>
                <span className="text-[11px] text-muted-foreground">
                  {mine.length} usecase(s) · {sum(mine, (r) => r.compliance.days)} day(s) ·{" "}
                  {mine.filter((r) => r.gaps.length).length} still to declare their scale
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-5">
                <span>
                  <span className="label-caps block">Cumulative</span>
                  <span className="font-data text-sm font-bold">
                    {pcts.length
                      ? fmtPct(pcts.reduce((t, p) => t + p, 0) / pcts.length)
                      : "N/A"}
                  </span>
                </span>
                <span>
                  <span className="label-caps block">Impact so far</span>
                  <span className="font-data text-sm font-bold">
                    {fmtLakhValue(sum(mine, (r) => r.impact.total_lakh))}
                  </span>
                </span>
                <span>
                  <span className="label-caps block">Last filled</span>
                  <span className="font-data text-sm font-bold">
                    {latest ? fmtDay(latest) : "never"}
                  </span>
                </span>
              </span>
            </button>
            {isOpen && (
              <div className="bg-muted/20 px-4 pb-3" data-testid={`compliance-unit-rows-${unit}`}>
                <table className="w-full min-w-[520px] text-xs">
                  <thead>
                    <tr className="text-left">
                      <th className="label-caps py-2">Use case</th>
                      <th className="label-caps py-2">KPI</th>
                      <th className="label-caps py-2">Days</th>
                      <th className="label-caps py-2">Cumulative</th>
                      <th className="label-caps py-2">Last filled · by whom</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {mine.map((r) => (
                      <tr key={r.code} data-testid={`compliance-unit-line-${r.code}`}>
                        <td className="py-2 pr-3">
                          <span className="font-data text-[10px] text-muted-foreground">
                            {r.code}
                          </span>
                          <span className="block font-semibold">{r.title}</span>
                        </td>
                        <td className="py-2 pr-3">{r.kpi.name || "not named"}</td>
                        <td className="font-data py-2 pr-3 tabular-nums">{r.compliance.days}</td>
                        <td className="font-data py-2 pr-3 tabular-nums">
                          {fmtPct(r.compliance.cumulative_pct)}
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {r.last_updated ? fmtDay(r.last_updated) : "never"}
                          {r.last_updated_by ? ` · ${r.last_updated_by}` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
